import { useT } from "../i18n/hooks.ts";
import type { LoadedProject } from "../model/project.ts";
import type { EvalHidProject } from "../model/types.ts";

function formatDateRange(t: ReturnType<typeof useT>, dates: string[] | undefined): string {
  if (!dates || dates.length === 0) return "—";
  return t("infoPopover.dateRange", {
    from: dates[0],
    to: dates[dates.length - 1],
    n: dates.length,
  });
}

/** Rainfall-runoff models actually used by the project's sub-basins (not the AQUATOOL catalog). */
function rainfallRunoffModelsUsed(project: EvalHidProject): string {
  const names = new Set(
    project.subBasins.map(
      (c) =>
        project.modelTypes.find((t) => t.modelTypeCode === c.modelTypeCode && t.kind === 0)
          ?.modelTypeName ?? `#${c.modelTypeCode}`
    )
  );
  return [...names].join(", ") || "—";
}

/** Loaded project summary, in a popover under the top bar's info icon. */
export function InfoPopover({ loadedProject }: { loadedProject: LoadedProject }) {
  const t = useT();
  const { project, series } = loadedProject;

  return (
    <div className="popover">
      <h3>{t("infoPopover.title")}</h3>
      <dl className="data-summary">
        <dt>{t("infoPopover.subBasins")}</dt>
        <dd>{project.subBasins.length}</dd>
        <dt>{t("infoPopover.aquifers")}</dt>
        <dd>{project.aquifers.length}</dd>
        <dt>{t("infoPopover.outletPoints")}</dt>
        <dd>{project.outletPoints.length}</dd>
        <dt>{t("infoPopover.models")}</dt>
        <dd>{rainfallRunoffModelsUsed(project)}</dd>
        <dt>{t("infoPopover.precipitation")}</dt>
        <dd>
          {series.precipitation
            ? formatDateRange(t, series.precipitation.dates)
            : t("infoPopover.notAvailable")}
        </dd>
        <dt>{t("infoPopover.etp")}</dt>
        <dd>{series.etp ? formatDateRange(t, series.etp.dates) : t("infoPopover.notAvailable")}</dd>
        <dt>{t("infoPopover.temperature")}</dt>
        <dd>
          {series.temperature
            ? formatDateRange(t, series.temperature.dates)
            : t("infoPopover.notProvidedOptionalSingular")}
        </dd>
        <dt>{t("infoPopover.pumping")}</dt>
        <dd>
          {series.pumping
            ? formatDateRange(t, series.pumping.dates)
            : t("infoPopover.notProvidedOptionalPlural")}
        </dd>
      </dl>
    </div>
  );
}
